import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { Avatar, AvatarFallback } from '../components/Avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/Tabs';
import { Button } from '../components/Button';
import { ScrollArea } from '../components/ScrollArea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Separator } from '../components/Separator';
import { Check, X, Star, Info, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
const worklist = [{
  id: '1',
  name: 'William George',
  initials: 'WG',
  mrn: '987654321',
  enc: 'ENC-2026-03-12',
  procedure: 'Primary THR (R)',
  admission: 'ELECTIVE',
  date: '12/03/2026',
  status: 'Pending'
}, {
  id: '2',
  name: 'Robert Miller',
  initials: 'RM',
  mrn: '123456789',
  enc: 'ENC-2026-03-11',
  procedure: 'Hemiarthroplasty (NOF)',
  admission: 'TRAUMA',
  date: '11/03/2026',
  status: 'In Progress'
}, {
  id: '3',
  name: 'Patricia Taylor',
  initials: 'PT',
  mrn: '456789123',
  enc: 'ENC-2026-03-10',
  procedure: 'Lumbar Decompression L4/5',
  admission: 'ELECTIVE',
  date: '10/03/2026',
  status: 'Done'
}, {
  id: '4',
  name: 'James Brown',
  initials: 'JB',
  mrn: '789123456',
  enc: 'ENC-2026-03-09',
  procedure: 'TKR (L) + Patellar Resurfacing',
  admission: 'ELECTIVE',
  date: '09/03/2026',
  status: 'Pending'
}, {
  id: '5',
  name: "Margaret O'Connor",
  initials: 'MO',
  mrn: '321654987',
  enc: 'ENC-2026-03-08',
  procedure: 'ORIF Distal Radius (R)',
  admission: 'TRAUMA',
  date: '08/03/2026',
  status: 'Done'
}];
const getAdmissionBadgeClass = (type: string) => {
  switch (type) {
    case 'TRAUMA':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'ELECTIVE':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'OBSRV':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    default:
      return '';
  }
};
const extractedCodes = [{
  code: 'M16.1',
  desc: 'Primary unilateral osteoarthritis of hip',
  type: 'ICD-10',
  locations: 4,
  primary: true
}, {
  code: 'W37.1',
  desc: 'Total prosthetic replacement of hip joint using cement',
  type: 'OPCS-4',
  locations: 3,
  primary: false
}, {
  code: 'Z96.64',
  desc: 'Presence of right artificial hip joint',
  type: 'ICD-10',
  locations: 2,
  primary: false
}, {
  code: 'Y70.4',
  desc: 'Spinal anaesthetic',
  type: 'OPCS-4',
  locations: 1,
  primary: false
}, {
  code: 'I10',
  desc: 'Essential (primary) hypertension',
  type: 'ICD-10',
  locations: 2,
  primary: false
}, {
  code: 'E11.9',
  desc: 'Type 2 diabetes mellitus without complications',
  type: 'ICD-10',
  locations: 1,
  primary: false
}];
export function BillingItems() {
  const [selectedPatient, setSelectedPatient] = useState<(typeof worklist)[0] | null>(worklist[0]);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedCode, setExpandedCode] = useState<number | null>(null);
  const filteredWorklist = worklist.filter(p => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return p.status === 'Pending';
    if (activeTab === 'in-progress') return p.status === 'In Progress';
    if (activeTab === 'done') return p.status === 'Done';
    return true;
  });
  return <div className="p-4 lg:p-6 max-w-[1600px] mx-auto h-[calc(100vh-64px)] flex flex-col space-y-4" data-id="element-42">
      <div className="flex-shrink-0" data-id="element-43">
        <h1 className="font-heading text-2xl sm:text-3xl font-normal text-foreground mb-1" data-id="element-44">
          Clinical Coding
        </h1>
        <p className="text-muted-foreground text-sm" data-id="element-45">
          AI-extracted orthopaedic ICD-10 & OPCS-4 codes mapped to billing
        </p>
      </div>

      <div className="flex gap-6 flex-1 min-h-0" data-id="element-46">
        {/* Left Panel: Worklist */}
        <div className="w-[320px] flex flex-col bg-card rounded-xl border border-border shadow-sm overflow-hidden flex-shrink-0" data-id="element-47">
          <div className="p-4 border-b border-border" data-id="element-48">
            <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="w-full" data-id="element-49">
              <TabsList className="w-full grid grid-cols-4 bg-secondary" data-id="element-50">
                <TabsTrigger value="all" className="text-xs" data-id="element-51">
                  All
                </TabsTrigger>
                <TabsTrigger value="pending" className="text-xs" data-id="element-52">
                  Pending
                </TabsTrigger>
                <TabsTrigger value="in-progress" className="text-xs" data-id="element-53">
                  In Prog
                </TabsTrigger>
                <TabsTrigger value="done" className="text-xs" data-id="element-54">
                  Done
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <ScrollArea className="flex-1" data-id="element-55">
            <div className="divide-y divide-border" data-id="element-56">
              {filteredWorklist.map(patient => <div key={patient.id} className={`p-4 cursor-pointer transition-colors hover:bg-secondary/50 ${selectedPatient?.id === patient.id ? 'bg-secondary border-l-4 border-l-primary' : 'border-l-4 border-l-transparent'}`} onClick={() => setSelectedPatient(patient)} data-id="element-57">
                  <div className="flex justify-between items-start mb-2" data-id="element-58">
                    <div className="font-medium text-foreground" data-id="element-59">
                      {patient.name}
                    </div>
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 ${getAdmissionBadgeClass(patient.admission)}`} data-id="element-60">
                      {patient.admission}
                    </Badge>
                  </div>
                  <div className="text-xs text-foreground/70 font-medium mb-1 truncate" data-id="element-61">
                    {patient.procedure}
                  </div>
                  <div className="text-[11px] text-muted-foreground flex justify-between" data-id="element-62">
                    <span className="truncate" data-id="element-63">{patient.mrn}</span>
                    <span data-id="element-64">{patient.date}</span>
                  </div>
                </div>)}
            </div>
          </ScrollArea>
        </div>

        {/* Right Panel: Workspace */}
        {selectedPatient ? <div className="flex-1 flex flex-col gap-6 min-h-0" data-id="element-65">
            {/* Patient Banner */}
            <Card className="flex-shrink-0 bg-primary text-primary-foreground border-none" data-id="element-66">
              <CardContent className="p-4 flex justify-between items-center" data-id="element-67">
                <div className="flex items-center gap-4" data-id="element-68">
                  <Avatar className="h-12 w-12 border-2 border-primary-foreground/20" data-id="element-69">
                    <AvatarFallback className="bg-primary-foreground text-primary font-bold text-lg" data-id="element-70">
                      {selectedPatient.initials}
                    </AvatarFallback>
                  </Avatar>
                  <div data-id="element-71">
                    <h2 className="font-heading text-2xl font-normal" data-id="element-72">
                      {selectedPatient.name}
                    </h2>
                    <div className="text-primary-foreground/90 text-sm font-medium" data-id="element-73">
                      {selectedPatient.procedure}
                    </div>
                    <div className="text-primary-foreground/70 text-xs flex gap-3 mt-0.5" data-id="element-74">
                      <span data-id="element-75">MRN: {selectedPatient.mrn}</span>
                      <span data-id="element-76">Encounter: {selectedPatient.enc}</span>
                      <span data-id="element-77">Discharged: {selectedPatient.date}</span>
                    </div>
                  </div>
                </div>
                <Badge className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30 border-none" data-id="element-78">
                  Status: {selectedPatient.status}
                </Badge>
              </CardContent>
            </Card>

            {/* Codes (left) & Billing Preview (right) */}
            <div className="flex gap-6 flex-1 min-h-0" data-id="element-79">
              {/* Medical Codes Panel - scrollable */}
              <Card className="flex flex-col flex-1 overflow-hidden" data-id="element-80">
                <CardHeader className="pb-0 flex-shrink-0" data-id="element-81">
                  <div className="flex justify-between items-center mb-4" data-id="element-82">
                    <CardTitle className="font-heading text-lg font-normal" data-id="element-83">
                      Medical Codes
                    </CardTitle>
                    <Badge variant="secondary" className="bg-primary/10 text-primary" data-id="element-84">
                      6 Suggested
                    </Badge>
                  </div>
                  <Tabs defaultValue="extracted" className="w-full" data-id="element-85">
                    <TabsList className="w-full grid grid-cols-3" data-id="element-86">
                      <TabsTrigger value="extracted" data-id="element-87">Extracted</TabsTrigger>
                      <TabsTrigger value="assigned" data-id="element-88">Assigned (0)</TabsTrigger>
                      <TabsTrigger value="rejected" data-id="element-89">Rejected (0)</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </CardHeader>
                <CardContent className="flex-1 overflow-hidden pt-4" data-id="element-90">
                  <ScrollArea className="h-full" data-id="element-91">
                    <div className="space-y-3 pb-4 pr-4" data-id="element-92">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs font-bold text-amber-800 flex items-center gap-2" data-id="element-93">
                        <Star className="w-4 h-4 fill-amber-500 text-amber-500" data-id="element-94" />
                        Primary Diagnosis (Suggested)
                      </div>

                      {extractedCodes.map((code, idx) => <Card key={idx} className={`border ${code.primary ? 'border-amber-300 shadow-sm' : 'border-border'} hover:border-primary transition-colors`} data-id="element-95">
                          <CardContent className="p-3" data-id="element-96">
                            <div className="flex items-start gap-3" data-id="element-97">
                              <div className="flex-1 min-w-0" data-id="element-98">
                                <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-99">
                                  <Badge variant="outline" className="font-mono bg-secondary" data-id="element-100">
                                    {code.code}
                                  </Badge>
                                  <Badge variant="secondary" className="text-[10px] h-5" data-id="element-101">
                                    {code.type}
                                  </Badge>
                                  {code.primary && <Star className="w-3 h-3 fill-amber-500 text-amber-500" data-id="element-102" />}
                                </div>
                                <p className="text-sm font-medium text-foreground leading-tight mb-2" data-id="element-103">
                                  {code.desc}
                                </p>

                                <button onClick={() => setExpandedCode(expandedCode === idx ? null : idx)} className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer hover:text-primary w-fit" data-id="element-104">
                                  <Info className="w-3 h-3" data-id="element-105" />
                                  Found in {code.locations} locations
                                  {expandedCode === idx ? <ChevronUp className="w-3 h-3 ml-1" data-id="element-106" /> : <ChevronDown className="w-3 h-3 ml-1" data-id="element-107" />}
                                </button>
                              </div>
                              <div className="flex flex-col gap-2 flex-shrink-0" data-id="element-108">
                                <Button size="icon" variant="outline" className="h-8 w-8 rounded-full text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200" data-id="element-109">
                                  <Check className="w-4 h-4" data-id="element-110" />
                                </Button>
                                <Button size="icon" variant="outline" className="h-8 w-8 rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" data-id="element-111">
                                  <X className="w-4 h-4" data-id="element-112" />
                                </Button>
                              </div>
                            </div>

                            {expandedCode === idx && <div className="mt-3 pt-3 border-t border-border space-y-2 bg-secondary/30 -mx-3 -mb-3 px-3 pb-3 rounded-b-lg" data-id="element-113">
                                <div className="flex items-center gap-2" data-id="element-114">
                                  <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-none text-[10px]" data-id="element-115">
                                    LLM — identified this code
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground" data-id="element-116">
                                  Source: Discharge Summary
                                </p>
                                <p className="text-sm italic border-l-2 border-primary pl-2 bg-card py-2 px-2 rounded-r" data-id="element-117">
                                  "...patient with longstanding right hip pain
                                  and reduced range of motion consistent with{' '}
                                  <strong className="text-foreground font-bold bg-amber-100 px-1 rounded" data-id="element-118">
                                    {code.desc.toLowerCase()}
                                  </strong>
                                  , confirmed on weight-bearing AP pelvis
                                  radiograph (Kellgren-Lawrence grade 4)..."
                                </p>
                              </div>}
                          </CardContent>
                        </Card>)}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Billing Document Preview - prominent right panel */}
              <Card className="flex flex-col w-[400px] flex-shrink-0 self-start" data-id="element-119">
                <CardHeader data-id="element-120">
                  <CardTitle className="font-heading text-lg font-normal" data-id="element-121">
                    Billing Document Preview
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col" data-id="element-122">
                  <div className="border border-border rounded-lg overflow-hidden flex flex-col bg-secondary/30" data-id="element-123">
                    <Table data-id="element-124">
                      <TableHeader className="bg-card" data-id="element-125">
                        <TableRow data-id="element-126">
                          <TableHead className="w-[80px]" data-id="element-127">Code</TableHead>
                          <TableHead data-id="element-128">Description</TableHead>
                          <TableHead className="text-right" data-id="element-129">Qty</TableHead>
                          <TableHead className="text-right" data-id="element-130">Net</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody className="bg-card" data-id="element-131">
                        <TableRow data-id="element-132">
                          <TableCell className="font-mono text-xs" data-id="element-133">
                            M16.1
                          </TableCell>
                          <TableCell className="text-xs truncate max-w-[150px]" data-id="element-134">
                            Primary osteoarthritis of hip
                          </TableCell>
                          <TableCell className="text-right text-xs" data-id="element-135">
                            1
                          </TableCell>
                          <TableCell className="text-right text-xs" data-id="element-136">
                            £6,200.00
                          </TableCell>
                        </TableRow>
                        <TableRow data-id="element-137">
                          <TableCell className="font-mono text-xs" data-id="element-138">
                            W37.1
                          </TableCell>
                          <TableCell className="text-xs truncate max-w-[150px]" data-id="element-139">
                            Total hip replacement (cemented)
                          </TableCell>
                          <TableCell className="text-right text-xs" data-id="element-140">
                            1
                          </TableCell>
                          <TableCell className="text-right text-xs" data-id="element-141">
                            £4,850.00
                          </TableCell>
                        </TableRow>
                        <TableRow data-id="element-142">
                          <TableCell className="font-mono text-xs" data-id="element-143">
                            Y70.4
                          </TableCell>
                          <TableCell className="text-xs truncate max-w-[150px]" data-id="element-144">
                            Spinal anaesthetic
                          </TableCell>
                          <TableCell className="text-right text-xs" data-id="element-145">
                            1
                          </TableCell>
                          <TableCell className="text-right text-xs" data-id="element-146">
                            £420.00
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    <div className="bg-card p-4 border-t border-border" data-id="element-147">
                      <div className="space-y-2 text-sm" data-id="element-148">
                        <div className="flex justify-between text-muted-foreground" data-id="element-149">
                          <span data-id="element-150">Total Billed:</span>
                          <span data-id="element-151">£11,470.00</span>
                        </div>
                        <div className="flex justify-between text-muted-foreground" data-id="element-152">
                          <span data-id="element-153">Paid Amount:</span>
                          <span data-id="element-154">£0.00</span>
                        </div>
                        <Separator className="my-2" data-id="element-155" />
                        <div className="flex justify-between font-bold text-foreground text-lg" data-id="element-156">
                          <span data-id="element-157">Due Amount:</span>
                          <span data-id="element-158">£11,470.00</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button className="w-full mt-4 bg-primary hover:bg-primary/90 text-primary-foreground gap-2 h-12 text-base" data-id="element-159">
                    Submit to Insurance
                    <ArrowRight className="w-5 h-5" data-id="element-160" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div> : <div className="flex-1 flex items-center justify-center text-muted-foreground bg-card rounded-xl border border-border" data-id="element-161">
            Select a patient from the worklist to view billing items.
          </div>}
      </div>
    </div>;
}